import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GetRowIdParams } from 'ag-grid-enterprise';
import { Observable, from, of } from 'rxjs';
import { LoadingMask } from 'src/app/classes/loading-mask';
import { Company, User } from 'src/app/classes/models';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';

import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { EditUserModalComponent } from '../edit-user-modal/edit-user-modal.component';

@Component({
  selector: 'app-client-users-upload-modal',
  templateUrl: './client-users-upload-modal.component.html',
  styleUrl: './client-users-upload-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientUsersUploadModalComponent implements OnInit, AfterViewInit {
  constructor(
    private activeModal: NgbActiveModal,
    private ModalService: NgbModal,
    private ToastService: ToastService,
    private HostElement: ElementRef,
    private UserService: UserService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {}

  @Input() Users?: User[];
  @Input() Company?: Company;

  title: string = 'Users Upload';
  missingRequiredDataCount: number = 0;
  matchingExistingUserCount: number = 0;
  bothMissingRequiredFieldsAndMatchingExistingCount: number = 0;

  rowData: Observable<any[]> = of([]);
  rowClassRules = {
    'gridRow-red': (params: any) =>
      params.data.missingRequiredData === true &&
      !params.data.matchingExistingUser,
    'gridRow-yellow': (params: any) =>
      params.data.matchingExistingUser === true &&
      !params.data.missingRequiredData,
    'gridRow-orange': (params: any) =>
      params.data.missingRequiredData === true &&
      params.data.matchingExistingUser === true
  };
  reloadGrid: boolean = false;
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'delete']
  };

  // Parsed users have id=null until saved, so the default getRowId
  // collapses every row to the same id and breaks per-row updates.
  private uploadRowIds = new WeakMap<object, string>();
  private nextUploadRowId = 0;

  getUserRowId = (params: GetRowIdParams) => {
    const data = params.data as object;
    let rowId = this.uploadRowIds.get(data);
    if (!rowId) {
      rowId = `upload-${this.nextUploadRowId++}`;
      this.uploadRowIds.set(data, rowId);
    }
    return rowId;
  };

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  upload() {
    if (this.getSumOfCounts() > 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload Users. There are errors in the uploaded file.',
        { returnError: true }
      );
    }

    if (!this.Users || this.Users.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload Users. There are no Users to upload.',
        { returnError: true }
      );
    }

    const { nativeElement } = this.HostElement;
    const mask = new LoadingMask(nativeElement, {
      maskText: 'Uploading Users...'
    });

    this.UserService.saveUsers(this.Users).subscribe({
      next: () => {
        mask.destroy();
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully uploaded Users file.'
        );
        this.activeModal.close();
      },
      error: () => {
        mask.destroy();
      }
    });
  }

  actionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (action === 'edit') {
      if (!this.Company) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing Company information.',
          {
            returnError: true
          }
        );
      }

      const modalRef = this.ModalService.open(EditUserModalComponent, {
        backdrop: 'static',
        centered: true,
        size: 'lg'
      });
      Object.assign(modalRef.componentInstance, {
        User: rowData,
        Company: this.Company
      });

      modalRef.closed.subscribe({
        next: (EditedUser) => {
          Object.assign(rowData, EditedUser);

          this.reloadGrid = true;
          this.calculateCounts(this.Users!);
          this.ChangeDetectorRef.markForCheck();
        }
      });
    }

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md', backdrop: 'static' }
      );
      Object.assign(componentInstance, {
        displayText:
          'Are you sure you want to remove this user from the upload?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            const index = this.Users?.findIndex((user) => user === rowData);

            if (index !== -1) {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully removed user from upload.'
              );
              this.Users?.splice(index!, 1);
              this.rowData = of([...this.Users!]);
              this.calculateCounts(this.Users!);
              this.reloadGrid = true;
              this.ChangeDetectorRef.markForCheck();
            } else {
              this.ToastService.addErrorToast(
                'Error',
                'No user found to remove.'
              );
            }
          }
        }
      });
    }
  }

  getSumOfCounts() {
    return (
      this.missingRequiredDataCount +
      this.matchingExistingUserCount +
      this.bothMissingRequiredFieldsAndMatchingExistingCount
    );
  }

  calculateCounts(Users: User[]) {
    if (!Users) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot calculate counts. No Users data found.',
        { returnError: true }
      );
    }

    this.missingRequiredDataCount = Users.filter(
      (user: any) =>
        user.missingRequiredData === true && !user.matchingExistingUser
    ).length;
    this.matchingExistingUserCount = Users.filter(
      (user: any) =>
        user.matchingExistingUser === true && !user.missingRequiredData
    ).length;
    this.bothMissingRequiredFieldsAndMatchingExistingCount = Users.filter(
      (user: any) =>
        user.matchingExistingUser === true && user.missingRequiredData === true
    ).length;
  }

  ngOnInit(): void {
    if (!this.Users || this.Users.length === 0) {
      this.activeModal.close();
      setTimeout(() => {
        this.ToastService.addErrorToast(
          'Error',
          'Cannot upload Users. No Users found.'
        );
        this.ChangeDetectorRef.detectChanges();
      });
      this.rowData = of([]);
      return;
    }

    this.rowData = of(this.Users);
    this.calculateCounts(this.Users);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.ChangeDetectorRef.detectChanges();
    }, 150);
  }
}
